import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Parent Cart — B2C retail cart.
 *
 * Line types:
 *   product line — a single catalog product
 *   kit line     — a school Book Kit purchased as ONE atomic unit (qty=1
 *                  per child). All item-level detail preserved for backend.
 *
 * Persisted to localStorage so shopping survives reload.
 */
const KEY = "schoollane:parent-cart:v3";
const CartContext = createContext();

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const add = (p, qty = 1) => setItems((prev) => {
    const ex = prev.find((x) => x.type === "product" && x.product_id === p.id);
    if (ex) return prev.map((x) =>
      x === ex ? { ...x, qty: x.qty + qty } : x);
    return [...prev, {
      type: "product",
      product_id: p.id,
      name: p.name,
      price: Number(p.price) || 0,
      mrp: Number(p.mrp) || 0,
      image: p.image,
      category: p.category,
      qty: Math.max(1, qty),
    }];
  });

  /** Add a complete Book Kit as a single atomic line (qty=1 per child). */
  const addKit = (kit) => setItems((prev) => {
    // If this kit is already in the cart, do nothing (kit is per-child, qty=1).
    if (prev.some((x) => x.type === "kit" && x.kit_id === kit.id)) return prev;
    const items = (kit.items || []).map((it) => ({
      product_id: it.product_id,
      name: it.product?.name,
      subject: it.product?.category,
      price: Number(it.unit_price) || 0,
      mrp: Number(it.product?.mrp) || 0,
      image: it.product?.image,
      qty: it.qty || 1,
      required: it.required,
    }));
    return [...prev, {
      type: "kit",
      kit_id: kit.id,
      kit_name: kit.name,
      kit_class: kit.class_level,
      kit_lang: kit.second_language,
      kit_combo: kit.subject_combination,
      kit_school_id: kit.school_id,
      kit_school_name: kit.school_name,
      kit_year_id: kit.academic_year_id,
      kit_year_name: kit.academic_year_name,
      description: kit.description,
      price: Number(kit.total_price) || items.reduce((s, i) => s + i.price * i.qty, 0),
      mrp: items.reduce((s, i) => s + (i.mrp || 0) * i.qty, 0),
      items,
      qty: 1,
    }];
  });

  const removeLine = (index) => setItems((p) => p.filter((_, i) => i !== index));
  const setProductQty = (index, qty) => setItems((p) => p.map((x, i) =>
    i === index && x.type === "product"
      ? { ...x, qty: Math.max(1, qty) } : x));

  const clear = () => setItems([]);

  const total = items.reduce((s, x) => s + (Number(x.price) || 0) * (x.qty || 1), 0);
  const mrpTotal = items.reduce((s, x) => s + (Number(x.mrp) || 0) * (x.qty || 1), 0);
  const savings = Math.max(0, mrpTotal - total);
  const totalUnits = items.reduce((s, x) =>
    s + (x.type === "kit" ? (x.items || []).reduce((k, i) => k + i.qty, 0) : x.qty), 0);
  const lineCount = items.length;

  /** Server-safe payload for POST /api/orders — a FLAT list of {product_id, qty}. */
  const orderItems = () => {
    const out = [];
    for (const x of items) {
      if (x.type === "product") {
        out.push({ product_id: x.product_id, qty: x.qty });
      } else if (x.type === "kit") {
        for (const i of x.items) {
          out.push({ product_id: i.product_id, qty: i.qty });
        }
      }
    }
    return out;
  };

  return (
    <CartContext.Provider value={{
      items, add, addKit, removeLine, setProductQty, clear,
      total, mrpTotal, savings, totalUnits, lineCount, orderItems,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
